<template>
  <div>
    <component
      :is="componentsBySizes[screenSize]"
      :ref="screenSize"
      :data="data"
      :update-field="updateField"
      :title="title"
      :first-time="firstTime"
      :handle-error="handleError"
      :handle-click-canada="handleClickCanada"
      :handle-click-mexico="handleClickMexico"
      :canada="canada"
      :mexico="mexico"
      :regions="regions"
      :add-region="addRegion"
      :remove-region="removeRegion"
      :inspected="inspected"
      :preferred="preferred"
      :preferred-by-region="preferredByRegion"
      :add-preferred="addPreferred"
      :remove-preferred="removePreferred"
      :add-preferred-and-region="addPreferredAndRegion"
      :remove-preferred-and-region="removePreferredAndRegion"
      :screen-size="screenSize"
      :step-name="stepName"
      :update-lanes-packet="updateLanesPacket"
    />
    <form-navigation-buttons
      v-if="!isNavigationDisabled"
      :enabled="!isNavigationDisabled"
      :component-identifier="componentIdentifier"
      :buttons-to-display="buttonsToDisplay"
      :is-button-disabled="isButtonDisabled"
      @next="handleNext"
      @back="handleBack"
      @edit="handleEdit"
      @exit-modal="emitExitModal"
    />
  </div>
</template>

<script>
import FormNavigationButtons from '@/components/FormNavigationButtons.vue';
import { useFormRouter } from '@/composables/formRouter';
import { useStore } from 'vuex';
import { computed, ref, watch } from 'vue';
import LanesMobile from './LanesMobile.vue';
import LanesDesktop from './LanesDesktop.vue';
import LanesTablet from './LanesTablet.vue';
import stepInformation from '@/common/stepInformation';
import usStates from '@/common/usStates';
import moment from 'moment';

export default {
  components: {
    'lanes-mobile': LanesMobile,
    'lanes-desktop': LanesDesktop,
    'lanes-tablet': LanesTablet,
    FormNavigationButtons,
  },
  props: {
    data: Object,
    updateField: Function,
    nextStep: Function,
    skipStep: Function,
    formPosition: Number,
    title: String,
    stepName: String,
    screenSize: String,
  },
  setup(_, { emit }) {
    const store = useStore();
    const componentIdentifier = 'Lanes';
    const formRouter = useFormRouter(componentIdentifier);
    const componentStep = computed(() => formRouter.componentStep.value);
    const isNavigationDisabled = computed(() => formRouter.isDisabled.value);
    const firstTime = computed(
      () => store.state.firstTime?.[componentStep.value?.firstTimeIdentifier]
    );
    const buttonsToDisplay = computed(() => {
      if (firstTime.value) return 'continue-save';
      return 'continue-edit';
    });
    const isButtonDisabled = false;
    const stepDirection = computed(() => store.state.formRoutes.stepDirection);
    const onboarding_id = computed(() => store.state.carriers?.carrierData?.id);

    const handleNext = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleNext' called"
      );
      if (isNavigationDisabled.value)
        return store.commit(
          'addFormDebugMessage',
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );
      store.commit('firstTime/updateStepFirstTime', {
        step: 'Lanes',
        value: false,
      });
      formRouter.moveToNextStep();
    };

    const handleBack = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleBack' called"
      );
      if (isNavigationDisabled.value)
        return store.commit(
          'addFormDebugMessage',
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );
      formRouter.moveToPreviousStep();
    };

    const handleEdit = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleEdit' called"
      );
      if (isNavigationDisabled.value)
        return console.log(
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );
      formRouter.editActiveStep();
    };

    const redirectChecks = ref(null);

    const redirectCheckDataHasLoaded = computed(() => {
      return onboarding_id.value > 0;
    });

    const redirectCheck1 = computed(
      () => store.state.carriers?.carrierData?.identity?.has_european_identifier
    );

    const runRedirectCheck = async () => {
      // 1. Skip Lanes Step if Carrier has European Identifier
      store.commit(
        'addFormDebugMessage',
        'REDIRECT CHECK 1 | ' +
          componentIdentifier +
          ': Skip Lanes Step if Carrier has European Identifier'
      );
      store.commit(
        'addFormDebugMessage',
        'REDIRECT CHECK 1 | ' +
          componentIdentifier +
          ": Description: Will redirect if Carrier's has_european_identifier value is true"
      );
      if (redirectCheck1.value) {
        store.commit(
          'addFormDebugMessage',
          'REDIRECT CHECK 1 | ' +
            componentIdentifier +
            ': Redirect necessary, skipping forward to Certifications-1'
        );
        return {
          redirect_check_number: 1,
          redirect_next_identifier: 'Certifications-1',
          redirect_back_identifier: 'Operations',
        };
      } else {
        store.commit(
          'addFormDebugMessage',
          'REDIRECT CHECK 1 | ' +
            componentIdentifier +
            ': No redirect necessary'
        );
      }
      return false;
    };

    watch(isNavigationDisabled, async (val1) => {
      if (val1 == false) {
        if (redirectCheckDataHasLoaded.value !== true) {
          store.commit(
            'addFormDebugMessage',
            'WAITING FOR REDIRECT CHECKS | ' +
              componentIdentifier +
              ': (isNavigationDisabled watcher) ' +
              componentIdentifier +
              ' is active step, but waiting for carrier identity data to load'
          );
        } else {
          store.commit(
            'addFormDebugMessage',
            'BEGIN REDIRECT CHECKS | ' +
              componentIdentifier +
              ': (isNavigationDisabled watcher) ' +
              componentIdentifier +
              ' is active step, running redirect checks'
          );
          redirectChecks.value = await runRedirectCheck();
          if (redirectChecks.value !== false) {
            handleRedirect(redirectChecks.value);
          }
        }
      }
    });

    const handleRedirect = async (redirect_check) => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleRedirect' called"
      );
      if (isNavigationDisabled.value)
        return store.commit(
          'addFormDebugMessage',
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );
      let redirectIdentifier;
      if (stepDirection.value == 'back') {
        redirectIdentifier = redirect_check?.redirect_back_identifier;
      } else {
        redirectIdentifier = redirect_check?.redirect_next_identifier;
        if (redirect_check?.redirect_check_number == 1) {
          store.commit(
            'addFormDebugMessage',
            'NEXT WITHOUT DATA | REDIRECT CHECK ' +
              redirect_check?.redirect_check_number +
              ' | Forward | ' +
              componentIdentifier +
              ': No data to save, but skipping Lanes step, moving to Certifications-1, and enabling submit button'
          );
          // enable submit button
          store.commit('toggleDisabledButton', {
            bool: false,
            source: 'Lanes #11',
          });
          await store.dispatch('onboardingPacket/nextStepPatch', {
            id: onboarding_id.value,
          });
        }
      }
      store.commit(
        'addFormDebugMessage',
        'REDIRECT CHECK ' +
          redirect_check?.redirect_check_number +
          ' | ' +
          componentIdentifier +
          ': Redirecting to ' +
          redirectIdentifier
      );
      formRouter.moveToStepByIdentifier(redirectIdentifier);
    };

    const emitExitModal = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'emitExitModal' called"
      );
      emit('exit-modal');
    };

    return {
      componentIdentifier,
      handleNext,
      handleBack,
      handleEdit,
      buttonsToDisplay,
      isButtonDisabled,
      isNavigationDisabled,
      firstTime,
      componentStep,
      emitExitModal,
    };
  },
  data() {
    return {
      componentsBySizes: {
        mobile: 'lanes-mobile',
        desktop: 'lanes-desktop',
        tablet: 'lanes-tablet',
      },
      preferredByRegion: {
        Midwest: [],
        Southwest: [],
        Southeast: [],
        'Rocky Mountain': [],
        Pacific: [],
        Northeast: [],
      },
      regions: [],
      preferred: [],
      inspected: [],
      errors: {
        selected: true,
      },
      canada: false,
      mexico: false,
      currentComponent: '',
      executed: {
        tablet: false,
        desktop: false,
      },
      dataAvailable: false,
    };
  },
  computed: {
    errorsExist() {
      return !this.preferred.length;
    },
    identifiers() {
      return (
        this.$store.state.carriers?.carrierData?.identity?.identifiers || []
      );
    },
    canadianIdentifiers() {
      let allIdentifiers = this.$store.state.identifiers?.allIdentifiers || [];
      let keys = [];
      if (allIdentifiers?.canadian_identifiers) {
        for (const [key] of Object.entries(
          allIdentifiers?.canadian_identifiers
        )) {
          keys.push(key);
        }
      }
      return keys;
    },
    mexicanIdentifiers() {
      let allIdentifiers = this.$store.state.identifiers?.allIdentifiers || [];
      let keys = [];
      if (allIdentifiers?.mexican_identifiers) {
        for (const [key] of Object.entries(
          allIdentifiers?.mexican_identifiers
        )) {
          keys.push(key);
        }
      }
      return keys;
    },
  },
  watch: {
    canadianIdentifiers: function (val) {
      if (val?.length > 0 && this.stepName === 'Lanes') {
        this.checkForeign();
      }
    },
    stepName: function (val) {
      if (val === 'Lanes') {
        const findMexico =
          this.data.lanes.latest_onboarding_lane_preferences.find(
            (el) => el.area_id === 55111
          );
        const findCanada =
          this.data.lanes.latest_onboarding_lane_preferences.find(
            (el) => el.area_id === 55110
          );

        if (findMexico) {
          this.mexico = true;
        }
        if (findCanada) {
          this.canada = true;
        }

        let unfilteredPref =
          this.data.lanes.latest_onboarding_lane_preferences.map((el) => {
            if (![55110, 55111].includes(el.area_id)) {
              return { ...el.area, area_id: el.area_id };
            }
          });
        this.preferred = unfilteredPref.filter((el) => el !== undefined);

        this.inspected = this.data.lanes.by_state.inspection_areas;
        let regionByStates = stepInformation.lanes.statesByRegion;
        let statesToRegionHash = {};

        Object.entries(regionByStates).forEach(([key, value]) => {
          value.forEach((state) => (statesToRegionHash[state] = key));
        });

        this.preferred.forEach((el) => {
          if (!this.regions.includes(statesToRegionHash[el.code]))
            this.regions.push(statesToRegionHash[el.code]);
        });

        if (!this.$store.state.firstTime?.['Lanes']) {
          this.canada = Object.values(
            this.data.lanes.latest_onboarding_lane_preferences
          ).some((el) => el.area_id === 55110)
            ? true
            : false;
          this.mexico = Object.values(
            this.data.lanes.latest_onboarding_lane_preferences
          ).some((el) => el.area_id === 55111)
            ? true
            : false;
        }

        if (
          this.regions.length === 0
          // && this.firstTime
          //  && (!findCanada || !findMexico)
        ) {
          this.$store.commit('toggleDisabledButton', {
            bool: true,
            source: 'Lanes #7',
          });
        } else {
          this.$store.commit('toggleDisabledButton', {
            bool: false,
            source: 'Lanes #8',
          });
        }
        // this.$store.commit("toggleDisabledButton", false);

        if (this.identifiers?.length > 0) {
          this.checkForeign();
        }
      }
    },
    errorsExist: function (nv) {
      !nv
        ? this.$store.commit('toggleDisabledButton', {
            bool: false,
            source: 'Lanes #9',
          })
        : this.$store.commit('toggleDisabledButton', {
            bool: true,
            source: 'Lanes #10',
          });
    },
  },
  mounted() {
    if (this.stepName === 'Lanes') {
      this.$store.commit('toggleDisabledButton', {
        bool: false,
        source: 'Lanes #1',
      });
    }
  },
  methods: {
    handleError(err, value) {
      this.errors[err] = value;
    },
    checkIfButtonNeedsToBeEnable() {
      if (this.regions.length) {
        this.$store.commit('toggleDisabledButton', {
          bool: false,
          source: 'Lanes #2',
        });
      } else {
        this.$store.commit('toggleDisabledButton', {
          bool: true,
          source: 'Lanes #3',
        });
      }
    },
    handleClickCanada(e) {
      const canadaVal = e.target.value === 'true' ? true : false;
      // this.errors.canada = false;
      this.canada = canadaVal;
      let body = this.createCountryBody('canada', canadaVal);
      if (body) this.updateOnboardingData(body);
      this.$store.commit('toggleDisabledButton', {
        bool: false,
        source: 'Lanes #4',
      });
      // this.checkIfButtonNeedsToBeEnable();
    },
    handleClickMexico(e) {
      const mexicoVal = e.target.value === 'true' ? true : false;
      // this.errors.mexico = false;
      this.mexico = mexicoVal;
      let body = this.createCountryBody('mexico', mexicoVal);
      if (body) this.updateOnboardingData(body);
      this.$store.commit('toggleDisabledButton', {
        bool: false,
        source: 'Lanes #5',
      });
      // this.checkIfButtonNeedsToBeEnable();
    },
    createCountryBody(country, countryVal) {
      let areaCode = country === 'canada' ? 55110 : 55111;
      let connection_id = this.$store.state.onboardingPacket?.id;
      let date = new Date();
      let currentDate = moment(date, 'YYYY-MM-DD').format('YYYY-MM-DD');
      let countryObj =
        this.data.lanes.latest_onboarding_lane_preferences.filter(
          (el) => el.area_id === areaCode
        );
      if (countryVal) {
        // if value is true
        if (!countryObj.length) {
          // and lanes doesnt include canada

          // add canada
          return {
            lane_preferenceable_type: 'OnboardingPacket',
            lane_preferenceable_id: connection_id,
            area_id: areaCode,
            file_date: currentDate,
            _destroy: 0,
          };
        } // if it does include canada, do nothing
      } else {
        // if value is false
        if (countryObj.length) {
          // and lane includes canada

          return {
            _destroy: 1,
            id: countryObj[0].id,
          };
        } // if lanes doesnt include canada, do nothing
      }
    },
    updateOnboardingData(body) {
      let payload = {};
      payload['body'] = {
        carrier: {
          lane_preferences_attributes: [body],
        },
      };
      payload['id'] = this.$store.state.onboardingPacket?.id;
      payload['no_reset'] = true;
      this.$store.commit(
        'addFormDebugMessage',
        'AUTO PACKET UPDATE | Lanes: (updateOnboardingData) carrier.lane_preferences_attributes data sent to server'
      );
      this.$store.dispatch('onboardingPacket/updatePacket', payload);
    },
    updateNestedField(path, value) {
      this.$store.commit('updateNestedField', { path, value });
    },
    addRegion(region) {
      let newRegionArr = this.regions || [];
      if (!newRegionArr.includes(region)) newRegionArr.push(region);
      this.regions = newRegionArr;
    },
    removeRegion(region) {
      let newRegionArr = this.regions || [];
      let filtered = newRegionArr.filter((el) => el !== region);
      this.regions = filtered;
    },
    addPreferred(state) {
      let newPreferred = this.preferred;
      newPreferred.push({ ...usStates[state], area_id: usStates[state].id });
      this.preferred = newPreferred;
      this.updateLanesPacket();
    },
    removePreferred(state) {
      let newPreferred = this.preferred;
      let filtered = newPreferred.filter((el) => el.code !== state);
      this.preferred = filtered;
      this.updateLanesPacket();
    },
    addPreferredAndRegion(state, region) {
      this.addPreferred(state);
      if (!this.preferredByRegion[region].includes(state))
        this.preferredByRegion[region].push(state);
    },
    removePreferredAndRegion(state, region) {
      this.removePreferred(state);
      this.preferredByRegion[region] = this.preferredByRegion[region].filter(
        (el) => state !== el
      );
      if (!this.preferredByRegion[region].length) this.removeRegion(region);
    },
    createOnboardingPacket() {
      // TODO: The naming of this variable connection_id doesn't make
      // sense
      let connection_id = this.$store.state.onboardingPacket?.id;
      let date = new Date();
      let currentDate = moment(date, 'YYYY-MM-DD').format('YYYY-MM-DD');
      let updates = [];

      let OnboardingPreferredData =
        this.data.lanes.latest_onboarding_lane_preferences.reduce(
          (map, obj) => {
            if (![55110, 55111].includes(obj.area_id)) {
              map[obj.area_id] = { ...obj.area, id: obj.id };
            }
            return map;
          },
          {}
        );

      this.preferred.forEach((el) => {
        if (OnboardingPreferredData[el.area_id] === undefined) {
          let createRequest = {
            lane_preferenceable_type: 'OnboardingPacket',
            lane_preferenceable_id: connection_id,
            area_id: el.area_id,
            file_date: currentDate,
            _destroy: 0,
          };
          updates.push(createRequest);
        } else {
          delete OnboardingPreferredData[el.area_id];
        }
      });

      Object.values(OnboardingPreferredData).forEach((el) => {
        let destroyRequests = {
          id: el.id,
          _destroy: 1,
        };
        updates.push(destroyRequests);
      });

      return {
        lane_preferences_attributes: updates,
      };
    },
    updateOnboardingPacket(body) {
      const date = new Date();
      const currentDate = moment(date, 'YYYY-MM-DD').format('YYYY-MM-DD');
      const today = moment();
      const findOldFileDate =
        this.$store.state.carriers.carrierData.lanes.latest_onboarding_lane_preferences?.reduce(
          (arr, item) => {
            const dateIsBefore = moment(item.created_at).isBefore(today, 'day');
            if (dateIsBefore) {
              arr.push({
                lane_preferenceable_type: 'OnboardingPacket',
                lane_preferenceable_id:
                  this.$store.state.carriers.carrierData.connection_id,
                area_id: item.area_id,
                file_date: currentDate,
              });
            }
            return arr;
          },
          []
        );
      const combinedBody = {
        lane_preferences_attributes: [
          ...body.lane_preferences_attributes,
          ...findOldFileDate,
        ],
      };
      this.$store.dispatch('onboardingPacket/setOnboardingBody', {
        carrier: combinedBody,
      });
    },
    updateLanesPacket() {
      let lanesPacket = this.createOnboardingPacket();
      this.updateOnboardingPacket(lanesPacket);
    },
    checkForeign() {
      let self = this;
      if (self?.identifiers?.length > 0) {
        let spreadIdentifiers = [...self.identifiers];
        let isMexicanCarrier = spreadIdentifiers.find((el) =>
          this.mexicanIdentifiers?.includes(el?.is_type)
        );
        let isCanadianCarrier = spreadIdentifiers.find((el) =>
          this.mexicanIdentifiers?.includes(el?.is_type)
        );
        if (isMexicanCarrier || isCanadianCarrier) {
          self.$store.commit('toggleDisabledButton', {
            bool: false,
            source: 'Lanes #6',
          });
        }
      }
    },
  },
};
</script>

<style></style>
